import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { useContent } from "@/lib/ContentProvider";

/**
 * GiftPrint V2 — carte cadeau imprimable A5 portrait, une seule page.
 *
 * Améliorations V2 :
 *  - Contenu contraint strictement à 210 mm (A5 vertical) → jamais de 2ème page.
 *  - Photo principale du refuge en grand format en haut.
 *  - Message personnalisé (phrase occasion française).
 *  - Résumé du cadeau clair, sans le prix.
 *  - Code cadeau typographié + QR menant à l'expérience digitale.
 *  - Date de validité, identité Curieux Refuge et mention « sous réserve ».
 *
 * Route : /bon-cadeau/:code/print
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Miroir de occasionPhrase() du voucher digital
function occasionPhrase(raw) {
  const k = (raw || "").toLowerCase().trim();
  if (!k) return "Simplement pour vous faire plaisir.";
  if (/anniv/.test(k))       return "Pour célébrer votre anniversaire.";
  if (/mariage|noce/.test(k))return "Pour votre mariage.";
  if (/lune de miel|honeymoon/.test(k)) return "Pour votre lune de miel.";
  if (/valentin/.test(k))    return "Pour votre Saint-Valentin.";
  if (/noel|noël|nowel/.test(k)) return "Pour Noël.";
  if (/nouvel an|new year|reveillon|réveillon/.test(k)) return "Pour le Nouvel An.";
  if (/merci|remerc/.test(k))return "Pour vous remercier.";
  if (/felic|félic/.test(k)) return "Pour vous féliciter.";
  if (/depart|départ|retraite/.test(k)) return "Pour marquer ce nouveau départ.";
  if (/naissance/.test(k))   return "Pour célébrer cette naissance.";
  if (/surprise/.test(k))    return "Simplement pour vous surprendre.";
  return `Pour ${raw.trim().toLowerCase()}.`;
}

export default function GiftPrint() {
  const { code } = useParams();
  const { content, mediaUrl } = useContent();
  const [card, setCard] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.getGiftCard(code)
      .then(setCard)
      .catch((e) => setErr(e?.response?.data?.detail || "Bon introuvable"));
  }, [code]);

  useEffect(() => {
    document.title = card
      ? `Carte cadeau · ${card.code}`
      : "Carte cadeau — Curieux Refuge";
  }, [card]);

  if (err) return <div style={{ padding: 40, textAlign: "center" }}>{err}</div>;
  if (!card) return <div style={{ padding: 40, textAlign: "center" }}>Chargement…</div>;

  const beneficiary = card.beneficiary?.firstName || "";
  const offerant = card.offerant?.firstName || "";
  const options = card.options || {};
  const prestations = [];
  if (options.petitDej) prestations.push("Petit-déjeuner inclus");
  if (options.bain) prestations.push("Bain de fleurs inclus");
  if (options.deco) prestations.push("Décor du Refuge inclus");

  const digitalUrl = `${window.location.origin}/bon-cadeau/${card.code}`;
  const phrase = occasionPhrase(card.occasion);

  // Photo principale de la carte (config globale content.giftMedia.printHero)
  const cfgHero = content?.giftMedia?.printHero;
  const heroSrc = cfgHero
    ? (cfgHero.startsWith("/") || cfgHero.startsWith("http")
        ? cfgHero
        : (mediaUrl ? mediaUrl(`/${cfgHero}`) : `${BACKEND}/api/media/${encodeURIComponent(cfgHero)}`))
    : `${BACKEND}/api/media/bain-de-fleurs.png`;

  return (
    <>
      {/* ===== Barre de contrôle (masquée à l'impression) ===== */}
      <div
        className="no-print"
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          padding: "10px 16px",
          background: "#FAF4EC",
          borderBottom: "1px solid rgba(139, 37, 48, 0.15)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontFamily: "'Cormorant Garamond', serif",
        }}
      >
        <div style={{ fontSize: 13, color: "#6B584C", fontStyle: "italic" }}>
          Carte cadeau · A5 vertical · une seule page
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="print-btn"
          style={{
            padding: "8px 20px",
            background: "#8B2530",
            color: "#FAF4EC",
            border: "none",
            borderRadius: 999,
            fontSize: 12,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Enregistrer / Imprimer
        </button>
      </div>

      {/* ===== La carte cadeau A5 ===== */}
      <div
        className="print-page"
        style={{
          margin: "60px auto 40px",
          width: "148mm",
          height: "210mm",     // hauteur FIXE pour verrouiller sur une page
          maxHeight: "210mm",
          overflow: "hidden",
          background: "#FDF9F0",
          color: "#2E1F18",
          fontFamily: "'Cormorant Garamond', serif",
          position: "relative",
          boxShadow: "0 20px 60px -30px rgba(0,0,0,0.3)",
          boxSizing: "border-box",
        }}
      >
        {/* Cadre doré double */}
        <div style={{
          position: "absolute", inset: "7mm",
          border: "1px solid rgba(184, 153, 104, 0.6)",
          borderRadius: 3,
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", inset: "8mm",
          border: "0.5px solid rgba(184, 153, 104, 0.35)",
          borderRadius: 2,
          pointerEvents: "none",
        }} />

        {/* Contenu — flex column pour distribuer proprement */}
        <div style={{
          position: "relative",
          zIndex: 2,
          height: "100%",
          padding: "11mm 12mm 8mm",
          display: "flex",
          flexDirection: "column",
        }}>
          {/* En-tête */}
          <div style={{ textAlign: "center", flexShrink: 0 }}>
            <div style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 9,
              letterSpacing: "0.42em",
              textTransform: "uppercase",
              color: "#8B2530",
              marginBottom: 4,
            }}>
              Curieux Refuge
            </div>
            <div style={{
              fontSize: 10,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: "#6B584C",
              fontFamily: "'Inter', sans-serif",
            }}>
              Carte cadeau
            </div>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
              <span style={{ display: "inline-block", height: 1, width: 34, background: "#B89968" }} />
            </div>
          </div>

          {/* Photo principale du refuge — grande */}
          <div style={{
            marginTop: "5mm",
            width: "100%",
            aspectRatio: "16 / 9",
            overflow: "hidden",
            borderRadius: 3,
            border: "1px solid rgba(184, 153, 104, 0.45)",
            flexShrink: 0,
          }}>
            <img
              src={heroSrc}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              crossOrigin="anonymous"
            />
          </div>

          {/* Bloc central — bénéficiaire + phrase */}
          <div style={{
            textAlign: "center",
            marginTop: "5mm",
            flexShrink: 0,
          }}>
            <div style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 8, letterSpacing: "0.32em",
              textTransform: "uppercase", color: "#8B2530", marginBottom: 3,
            }}>
              Pour
            </div>
            <div style={{
              fontSize: 26, fontStyle: "italic", fontWeight: 300,
              color: "#2E1F18", lineHeight: 1.05,
            }}>
              {beneficiary}
            </div>
            {offerant && (
              <div style={{
                marginTop: "2.5mm",
                fontStyle: "italic", fontSize: 11, color: "#8B2530",
                lineHeight: 1.4,
              }}>
                {offerant} vous offre un moment au Curieux Refuge.
              </div>
            )}
            <div style={{
              marginTop: "1.5mm",
              fontStyle: "italic", fontSize: 10, color: "#6B584C",
            }}>
              {phrase}
            </div>
          </div>

          {/* Contenu du cadeau — compact */}
          <div style={{
            textAlign: "center",
            margin: "5mm 3mm 0",
            padding: "3.5mm 4mm",
            background: "rgba(255,253,249,0.65)",
            border: "1px solid rgba(184, 153, 104, 0.4)",
            borderRadius: 3,
            flexShrink: 0,
          }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#2E1F18", lineHeight: 1.15 }}>
              {card.nights} {card.nights > 1 ? "nuits" : "nuit"} pour {card.travelers} {card.travelers > 1 ? "personnes" : "personne"}
            </div>
            <div style={{
              fontStyle: "italic", fontSize: 11, color: "#6B584C", marginTop: 1,
            }}>
              Formule {card.formule === "weekend" ? "Week-end" : "Semaine"}
            </div>
            {prestations.length > 0 && (
              <div style={{ marginTop: "2mm", fontSize: 10, color: "#2E1F18", lineHeight: 1.45 }}>
                {prestations.map((p) => <div key={p}>· {p}</div>)}
              </div>
            )}
          </div>

          {/* Espace flexible — pousse le pied de page vers le bas */}
          <div style={{ flex: 1, minHeight: "3mm" }} />

          {/* Pied : code + QR côte à côte */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: "5mm", margin: "0 2mm", flexShrink: 0,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 8, letterSpacing: "0.3em",
                textTransform: "uppercase", color: "#8B2530", marginBottom: 2,
              }}>
                Votre code cadeau
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13, letterSpacing: "0.12em", fontWeight: 500,
                color: "#2E1F18",
                padding: "2.5mm 3.5mm",
                background: "#FFFDF9",
                border: "1px dashed rgba(139, 37, 48, 0.55)",
                borderRadius: 3,
                display: "inline-block",
              }}>
                {card.code}
              </div>
              <div style={{
                fontSize: 9, fontStyle: "italic", color: "#6B584C", marginTop: 3,
              }}>
                Valable jusqu'au{" "}
                {card.validUntil
                  ? new Date(card.validUntil).toLocaleDateString("fr-FR", {
                      day: "numeric", month: "long", year: "numeric",
                    })
                  : "—"}
              </div>
            </div>
            <div style={{
              flexShrink: 0, padding: 2.5, background: "#fff",
              border: "1px solid rgba(184, 153, 104, 0.35)",
            }}>
              <QRCodeSVG value={digitalUrl} size={62} level="M" bgColor="#ffffff" fgColor="#2E1F18" />
            </div>
          </div>

          {/* Pied de carte */}
          <div style={{
            textAlign: "center", marginTop: "4mm",
            paddingTop: "2.5mm", borderTop: "1px solid rgba(184, 153, 104, 0.35)",
            flexShrink: 0,
          }}>
            <div style={{
              fontSize: 10, color: "#2E1F18",
            }}>
              Réservez sur <strong style={{ fontWeight: 500 }}>curieux-hub.emergent.host</strong>
            </div>
            <div style={{
              fontStyle: "italic", fontSize: 9, color: "#6B584C", marginTop: 1,
            }}>
              Sous réserve de disponibilité aux dates choisies
            </div>
            <div style={{
              marginTop: "1.5mm",
              fontStyle: "italic", fontSize: 10, color: "#8B2530",
            }}>
              Que la magie opère.
            </div>
          </div>
        </div>
      </div>

      {/* Styles print */}
      <style>{`
        @page { size: A5 portrait; margin: 0; }
        @media print {
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .print-page {
            margin: 0 !important;
            box-shadow: none !important;
            width: 148mm !important;
            height: 210mm !important;
            max-height: 210mm !important;
            page-break-after: avoid;
            page-break-inside: avoid;
          }
        }
        body { background: #E5DFD5; }
      `}</style>
    </>
  );
}
